import {
  CURRICULUM_ID,
  CURRICULUM_VERSION,
  type CurriculumManifest,
  type LanguageReviews,
  type Lesson,
  type LocalizedForms,
  type SpeakerVariant,
  type Sentence,
  type SentenceSegment,
  type VocabularyItem,
} from "../shared";
import {
  APPROVED_TOPIC_ORDER,
  STARTER_30_SEEDS,
  type ContentSeedEntry,
  type LessonSeed,
} from "./starter-30.data";

const PREVIEW_REVIEW_DATE = "2026-07-29";
const PREVIEW_REVIEWER = "Codex preview content migration";

function createReviews(): LanguageReviews {
  return {
    en: {
      status: "machine-reviewed",
      reviewedBy: PREVIEW_REVIEWER,
      reviewedAt: PREVIEW_REVIEW_DATE,
      source: "English draft adapted from the local learn-thai/data.js seed.",
    },
    nl: {
      status: "machine-reviewed",
      reviewedBy: PREVIEW_REVIEWER,
      reviewedAt: PREVIEW_REVIEW_DATE,
      source:
        "Dutch draft translated from the English seed for preview; human editorial review is required.",
    },
    th: {
      status: "machine-reviewed",
      reviewedBy: PREVIEW_REVIEWER,
      reviewedAt: PREVIEW_REVIEW_DATE,
      source:
        "Thai text adapted from the local learn-thai/data.js seed; native-speaker review is required.",
    },
    romanization: {
      status: "machine-reviewed",
      reviewedBy: PREVIEW_REVIEWER,
      reviewedAt: PREVIEW_REVIEW_DATE,
      source:
        "Custom romanization adapted from learn-thai/data.js; the notation is not yet a published standard.",
    },
  };
}

function createThaiSpeakerVariants(
  thai: string,
  english: string,
): SpeakerVariant[] | undefined {
  const trimmed = thai.trim();
  if (["ครับ", "ค่ะ", "คะ"].includes(trimmed)) return undefined;

  const isQuestion = english.trim().endsWith("?");
  const feminineParticle = isQuestion ? "คะ" : "ค่ะ";

  if (thai.includes("ครับ") || thai.includes("ผม")) {
    const feminine = thai
      .replaceAll("ผม", "ฉัน")
      .replaceAll("ครับ", feminineParticle);
    if (feminine === thai) return undefined;

    return [
      {
        speaker: "masculine",
        text: thai,
        usage: "Polite preview form typically used by a male speaker.",
      },
      {
        speaker: "feminine",
        text: feminine,
        usage: isQuestion
          ? "Polite preview question form typically used by a female speaker."
          : "Polite preview statement form typically used by a female speaker.",
      },
    ];
  }

  if (thai.includes("ค่ะ") || thai.includes("คะ") || thai.includes("ฉัน")) {
    const masculine = thai
      .replaceAll("ฉัน", "ผม")
      .replaceAll("ค่ะ", "ครับ")
      .replaceAll("คะ", "ครับ");
    if (masculine === thai) return undefined;

    return [
      {
        speaker: "masculine",
        text: masculine,
        usage: "Polite preview form typically used by a male speaker.",
      },
      {
        speaker: "feminine",
        text: thai,
        usage: isQuestion
          ? "Polite preview question form typically used by a female speaker."
          : "Polite preview statement form typically used by a female speaker.",
      },
    ];
  }

  return undefined;
}

function createForms(entry: ContentSeedEntry): LocalizedForms {
  const [, thai, romanization, english, dutch] = entry;
  const speakerVariants = createThaiSpeakerVariants(thai, english);

  return {
    en: { text: english },
    nl: { text: dutch },
    th: {
      text: thai,
      pronunciation: romanization,
      note: "Preview romanization; human review is required before publication.",
      ...(speakerVariants ? { speakerVariants } : {}),
    },
  };
}

function createVocabularyItem(
  seed: LessonSeed,
  dayLabel: string,
  entry: ContentSeedEntry,
  index: number,
): VocabularyItem {
  const [key] = entry;

  return {
    id: `starter-${dayLabel}-${seed.slug}-v-${key}`,
    meaningId: `meaning-vocabulary-${seed.slug}-${key}`,
    priority: index + 1,
    forms: createForms(entry),
    reviews: createReviews(),
  };
}

function createGreetingBreakdown(
  dayLabel: string,
  sentenceKey: string,
): SentenceSegment[] | undefined {
  const prefix = `starter-${dayLabel}-greetings`;
  const segment = (
    key: string,
    thai: string,
    pronunciation: string,
    english: string,
    dutch: string,
    vocabularyKey?: string,
  ): SentenceSegment => ({
    id: `${prefix}-segment-${sentenceKey}-${key}`,
    forms: {
      en: { text: english },
      nl: { text: dutch },
      th: { text: thai },
    },
    pronunciation,
    ...(vocabularyKey
      ? { vocabularyItemId: `${prefix}-v-${vocabularyKey}` }
      : {}),
  });

  if (sentenceKey === "hello-name-john") {
    return [
      segment("hello", "สวัสดี", "sà-wàt-dee", "Hello", "Hallo", "hello"),
      segment("male-pronoun", "ผม", "phǒm", "I", "ik"),
      segment("called", "ชื่อ", "chêu", "am called", "heet"),
      segment("john", "จอห์น", "John", "John", "John"),
    ];
  }

  if (sentenceKey === "thank-you-very-much") {
    return [
      segment(
        "thank-you",
        "ขอบคุณ",
        "khàwp-khun",
        "thank you",
        "bedankt",
        "thank-you",
      ),
      segment("very-much", "มาก", "mâak", "very much", "heel erg"),
    ];
  }

  return undefined;
}

function createSentence(
  seed: LessonSeed,
  dayLabel: string,
  entry: ContentSeedEntry,
  index: number,
): Sentence {
  const [key] = entry;
  const breakdown =
    seed.slug === "greetings"
      ? createGreetingBreakdown(dayLabel, key)
      : undefined;
  const highlightedVocabularyIds = [
    ...new Set(
      (breakdown ?? []).flatMap((segment) =>
        segment.vocabularyItemId ? [segment.vocabularyItemId] : [],
      ),
    ),
  ];

  return {
    id: `starter-${dayLabel}-${seed.slug}-s-${key}`,
    meaningId: `meaning-sentence-${seed.slug}-${key}`,
    priority: index + 1,
    forms: createForms(entry),
    highlightedVocabularyIds,
    ...(breakdown ? { breakdown } : {}),
    reviews: createReviews(),
  };
}

function createLesson(seed: LessonSeed, index: number): Lesson {
  const day = index + 1;
  const dayLabel = String(day).padStart(2, "0");

  return {
    id: `starter-${dayLabel}-${seed.slug}`,
    day,
    slug: seed.slug,
    emoji: seed.emoji,
    title: { ...seed.title },
    summary: seed.summary,
    publicationStatus: "preview",
    provenance:
      "Adapted from the local learn-thai/data.js seed. Thai, English, custom romanization, and Dutch drafts remain machine-reviewed preview content.",
    vocabulary: seed.vocabulary.map((entry, itemIndex) =>
      createVocabularyItem(seed, dayLabel, entry, itemIndex),
    ),
    sentences: seed.sentences.map((entry, itemIndex) =>
      createSentence(seed, dayLabel, entry, itemIndex),
    ),
  };
}

const SEEDS_BY_SLUG = new Map(
  STARTER_30_SEEDS.map((seed) => [seed.slug, seed] as const),
);

const ORDERED_SEEDS: LessonSeed[] = APPROVED_TOPIC_ORDER.map((slug) => {
  const seed = SEEDS_BY_SLUG.get(slug);
  if (!seed) {
    throw new Error(`Missing Starter 30 content seed for "${slug}".`);
  }
  return seed;
});

export const CURRICULUM_LESSONS: Lesson[] = ORDERED_SEEDS.map(createLesson);

export const CURRICULUM_MANIFEST: CurriculumManifest = {
  id: CURRICULUM_ID,
  version: CURRICULUM_VERSION,
  title: "SprinkleDays Starter 30 — Preview",
  supportedLanguages: ["en", "nl", "th"],
  publishedAt: PREVIEW_REVIEW_DATE,
  lessons: CURRICULUM_LESSONS.map((lesson) => ({
    id: lesson.id,
    day: lesson.day,
    slug: lesson.slug,
    emoji: lesson.emoji,
    title: { ...lesson.title },
    publicationStatus: lesson.publicationStatus,
    vocabularyCount: lesson.vocabulary.length,
    sentenceCount: lesson.sentences.length,
  })),
};

export const curriculumLessons = CURRICULUM_LESSONS;
export const curriculumManifest = CURRICULUM_MANIFEST;

const LESSONS_BY_DAY = new Map(
  CURRICULUM_LESSONS.map((lesson) => [lesson.day, lesson] as const),
);
const LESSONS_BY_ID = new Map(
  CURRICULUM_LESSONS.map((lesson) => [lesson.id, lesson] as const),
);
const LESSONS_BY_SLUG = new Map(
  CURRICULUM_LESSONS.map((lesson) => [lesson.slug, lesson] as const),
);

export function getAllLessons(): Lesson[] {
  return [...CURRICULUM_LESSONS];
}

export function getLessonByDay(day: number): Lesson | undefined {
  return LESSONS_BY_DAY.get(day);
}

export function getLessonById(id: string): Lesson | undefined {
  return LESSONS_BY_ID.get(id);
}

export function getLessonBySlug(slug: string): Lesson | undefined {
  return LESSONS_BY_SLUG.get(slug);
}

export function getLessonOrThrow(day: number): Lesson {
  const lesson = getLessonByDay(day);

  if (!lesson) {
    throw new RangeError(`No Starter 30 lesson exists for day ${day}.`);
  }

  return lesson;
}
